/* eslint-disable */
import React, { useEffect } from 'react';
import { Divider, Progress, Row, Typography } from 'antd';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import { useIsMobile, useIsTablet } from '~/hooks/useScreenSizes';
import WizardTopNavigation from '~/layouts/uae/WizardTopNavigation';
import { useDirection } from '~/hooks/i18nHooks';
import { usePageTitle } from '~/context/PageTitleContext';

function ContentHeader(props: {
  title: string;
  subtitle?: string;
  onBoarding?: boolean;
  stageNumber?: number;
  callSetTitle?: boolean;
  hideBackArrow?: boolean;
}) {
  const { title, subtitle, onBoarding, stageNumber = 0, callSetTitle = true, hideBackArrow } = props;
  const location = useLocation();
  const dir = useDirection();
  const isServices = location.pathname === '/ksa/products';
  const isMobile = useIsMobile();
  const percentage = 25 * stageNumber;
  const { setPageTitle } = usePageTitle();

  // Sync the real page title into context whenever it changes.
  // On unmount, reset to empty so the stale title doesn't leak into the next page's GTM event.
  useEffect(() => {
    if (callSetTitle) {
      setPageTitle(title);
    }
    return () => {
      setPageTitle('');
    };
  }, [title]);

  return (
    <Container
      dir={dir}
      isMobile={isMobile}
      onBoarding={onBoarding}
      isServices={isServices}
    >
      <TitleRow dir={dir}>
        {!isServices && !hideBackArrow && <WizardTopNavigation iconOnly />}
        <Typography.Paragraph className="title-Header">{title}</Typography.Paragraph>
      </TitleRow>
      <SubtitleWrapper dir={dir}>
        <Typography.Paragraph className="subtitle-Header">{subtitle}</Typography.Paragraph>
      </SubtitleWrapper>
      {isMobile && !!stageNumber ? (
        <ProgressWrapper>
          <Progress
            className={`progress-bar${dir === 'rtl' ? ' progress-bar--rtl' : ''}`}
            trailColor="white"
            showInfo={false}
            percent={percentage}
            strokeColor="rgba(47, 191, 113, 1)"
            strokeWidth={6}
          />
          <StageLabel dir={dir}>{stageNumber}/4</StageLabel>
        </ProgressWrapper>
      ) : (
        <Divider className="content-divider" />
      )}
    </Container>
  );
}

const TitleRow = styled.div<any>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;

  .WizardTopNavigation {
    display: flex;
    align-items: center;
    button {
      padding: 0 !important;
      height: 32px !important;
      width: 32px !important;
      min-width: unset !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
    }
  }
`;

const SubtitleWrapper = styled.div<{ dir?: string }>`
  text-align: ${({ dir }) => (dir === 'ltr' ? 'left' : 'right')};
`;

const ProgressWrapper = styled.div`
  position: relative;
  margin-top: -15px;
  height: 40px;

  .progress-bar {
    position: absolute;
    bottom: 0;
  }

  .progress-bar--rtl {
    right: 0;
  }
`;

const StageLabel = styled.span<{ dir?: string }>`
  position: absolute;
  top: 0;
  ${({ dir }) => (dir === 'rtl' ? 'left: 10px;' : 'right: 10px;')}
`;

const Container = styled.div<any>`
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  direction: ${({ dir }) => dir ?? 'ltr'};
  margin-bottom: ${({ isMobile }) => (isMobile ? '40px' : '24px')};

  .content-divider {
    margin-bottom: 0;
  }

  /* ── Shared base: properties common to both breakpoints ── */
  .title-Header {
    display: inline-block;
    font-size: 30px;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-weight: 500;
    line-height: 56px;
    margin: 0 !important;

    /* Desktop */
    color: #000;
    font-size: 38px;

    @media (max-width: 768px) {
      color: #070707;
      font-size: 30px;
    }
  }

  .subtitle-Header {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    color: #4A4A4A;

    /* Desktop */
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.14px;

    @media (max-width: 768px) {
      font-size:16px;
      line-height: 24px;
      letter-spacing: 0.16px;
      color: #000;
    }
  }
`;

export default ContentHeader;
